/**
 * Platform admin card: lifecycle email mode (disabled / test / live), the
 * test recipient, a test send, and outbox counts (/api/admin/lifecycle-email).
 */
import React, { useEffect, useState } from 'react';
import { TextInput, View } from 'react-native';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { AdminSectionCard } from '@/components/admin/AdminUI';
import { apiRequest } from '../../services/apiClient';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from '../../hooks/useTranslation';
import { showDialog } from '../../utils/dialogs';
import {
  canSendLifecycleTest,
  hasUnsavedLifecycleChanges,
  lifecycleTestSkipKey,
  type LifecycleEmailStatus,
  type LifecycleMode,
} from '../../utils/lifecycleEmail';

export default function LifecycleEmailCard() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [lifecycleEmail, setLifecycleEmail] = useState<LifecycleEmailStatus | null>(null);
  const [lifecycleMode, setLifecycleMode] = useState<LifecycleMode>('disabled');
  const [lifecycleRecipient, setLifecycleRecipient] = useState('');
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [testSkipMessage, setTestSkipMessage] = useState<string | null>(null);

  useEffect(() => {
    apiRequest<LifecycleEmailStatus>({ endpoint: '/admin/lifecycle-email' }).then((data) => {
      setLifecycleEmail(data); setLifecycleMode(data.mode); setLifecycleRecipient(data.test_recipient || '');
    }).catch(() => undefined);
  }, []);

  const saveLifecycleEmail = async () => {
    setLifecycleLoading(true);
    try {
      const data = await apiRequest<LifecycleEmailStatus>({ endpoint: '/admin/lifecycle-email', method: 'PUT',
        data: { mode: lifecycleMode, test_recipient: lifecycleMode === 'test' ? lifecycleRecipient : null } });
      setLifecycleEmail(data);
      setTestSkipMessage(null);
      showToast(t('platformAdmin.lifecycleSaved'), 'success');
    } catch (error: any) { showDialog(t('common.error'), error?.message || t('platformAdmin.lifecycleError')); }
    finally { setLifecycleLoading(false); }
  };

  const sendLifecycleTest = async () => {
    setLifecycleLoading(true);
    try {
      const result = await apiRequest<{ status: string; reason?: string }>({
        endpoint: '/admin/lifecycle-email/test-event', method: 'POST',
      });
      if (result?.status === 'skipped') {
        setTestSkipMessage(t(lifecycleTestSkipKey(result.reason)));
      } else {
        setTestSkipMessage(null);
        showToast(t('platformAdmin.lifecycleTestQueued'), 'success');
      }
    } catch (error: any) { showDialog(t('common.error'), error?.message || t('platformAdmin.lifecycleError')); }
    finally { setLifecycleLoading(false); }
  };

  const testReady = canSendLifecycleTest(lifecycleEmail, lifecycleMode, lifecycleRecipient);
  const showSaveHint = lifecycleMode === 'test' && hasUnsavedLifecycleChanges(lifecycleEmail, lifecycleMode, lifecycleRecipient);

  return (
    <AdminSectionCard icon="email" title={t('platformAdmin.lifecycleTitle')} subtitle={t('platformAdmin.lifecycleDescription')}>
      <View className="flex-row flex-wrap" style={{ gap: 8 }}>
        {(['disabled', 'test', 'live'] as const).map((mode) => (
          <Button key={mode} variant={lifecycleMode === mode ? 'default' : 'outline'} onPress={() => setLifecycleMode(mode)}>
            <Text className={lifecycleMode === mode ? 'text-white' : 'text-foreground'}>{t(`platformAdmin.lifecycleMode.${mode}`)}</Text>
          </Button>
        ))}
      </View>
      {lifecycleMode === 'test' ? <TextInput value={lifecycleRecipient} onChangeText={setLifecycleRecipient}
        placeholder={t('platformAdmin.lifecycleTestRecipient')} autoCapitalize="none" keyboardType="email-address"
        className="border border-gray-300 rounded px-3 py-2" /> : null}
      <View className="flex-row" style={{ gap: 8 }}>
        <Button disabled={lifecycleLoading} onPress={saveLifecycleEmail}><Text className="text-white">{t('common.save')}</Text></Button>
        <Button testID="lifecycle-send-test" variant="outline" disabled={lifecycleLoading || !testReady} onPress={sendLifecycleTest}>
          <Text>{t('platformAdmin.lifecycleSendTest')}</Text>
        </Button>
      </View>
      {showSaveHint ? <Text testID="lifecycle-save-hint" className="text-sm text-muted-foreground">
        {t('platformAdmin.lifecycleSaveBeforeTest')}
      </Text> : null}
      {testSkipMessage ? <Text testID="lifecycle-test-skipped" className="text-sm text-amber-700">{testSkipMessage}</Text> : null}
      {lifecycleEmail ? <Text className="text-sm text-muted-foreground">
        {t('platformAdmin.lifecycleCounts')}: {Object.entries(lifecycleEmail.outbox_counts || {}).map(([key, value]) => `${key} ${value}`).join(' · ')}
      </Text> : null}
      {lifecycleEmail?.last_error_sample ? <Text testID="lifecycle-last-error" className="text-sm text-red-700">
        {t('platformAdmin.lifecycleLastError')}: {lifecycleEmail.last_error_sample}
      </Text> : null}
    </AdminSectionCard>
  );
}
